import React, { useState, useEffect } from "react";
import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
  FormControlLabel,
  Switch,
  Divider,
  Button,
  Chip,
  Alert,
  CircularProgress
} from "@mui/material";
import { CheckCircleIcon, GetAppIcon, SaveIcon } from "../../icons";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../store/contexts";
import { useInstallPWA } from "../../common/hooks/useInstallPWA";
import { useServiceWorker } from "../../common/hooks/useServiceWorker";

export const PreferencesSettingsPage = observer(() => {
  const rootStore = useRootStore();
  const { langStore, prefStore, authStore } = rootStore;

  const [localLanguage, setLocalLanguage] = useState(langStore.language);
  const [localTheme, setLocalTheme] = useState<"light" | "dark">(prefStore.paletteType as "light" | "dark");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { isInstallable, isInstalled, installPrompt } = useInstallPWA();
  const { currentVersion } = useServiceWorker();

  useEffect(() => {
    setLocalLanguage(langStore.language);
    setLocalTheme(prefStore.paletteType as "light" | "dark");
  }, [langStore.language, prefStore.paletteType]);

  const hasChanges =
    localLanguage !== langStore.language ||
    localTheme !== prefStore.paletteType;

  const handleLanguageChange = (event: any) => {
    setLocalLanguage(event.target.value);
    setSaveSuccess(false);
  };

  const handleThemeToggle = () => {
    setLocalTheme(localTheme === "light" ? "dark" : "light");
    setSaveSuccess(false);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await authStore.saveUserSettings(localLanguage, localTheme);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save user settings:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleInstallClick = async () => {
    if (installPrompt) {
      await installPrompt();
    }
  };

  const isDarkMode = localTheme === 'dark';

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  return (
    <Box>
      <Stack spacing={4}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {langStore.languageLabel}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="language-select-label">
              {langStore.languageLabel}
            </InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={localLanguage}
              label={langStore.languageLabel}
              onChange={handleLanguageChange}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Español</MenuItem>
              <MenuItem value="pt">Português</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" gutterBottom>
            {langStore.themeLabel}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isDarkMode}
                onChange={handleThemeToggle}
                color="primary"
              />
            }
            label={isDarkMode ? langStore.darkModeLabel : langStore.lightModeLabel}
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isDarkMode
              ? "Dark mode is currently enabled"
              : "Light mode is currently enabled"}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveChanges}
            disabled={!hasChanges || saving}
            fullWidth
          >
            {saving ? langStore.savingLabel : langStore.saveChangesLabel}
          </Button>
          {saveSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {langStore.settingsSavedSuccessfullyLabel}
            </Alert>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" gutterBottom>
            Aplicación
          </Typography>

          {isInstalled && (
            <Chip
              icon={<CheckCircleIcon />}
              label="App instalada"
              color="success"
              sx={{ mt: 2 }}
            />
          )}

          {isInstallable && !isInstalled && (
            <Button
              variant="contained"
              startIcon={<GetAppIcon />}
              onClick={handleInstallClick}
              sx={{ mt: 2 }}
            >
              Instalar App
            </Button>
          )}

          {isIOS && !isInstalled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Para instalar en iOS: Tocá el botón de compartir y seleccioná "Agregar a pantalla de inicio"
            </Alert>
          )}

          {currentVersion && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Versión: {currentVersion}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
});
